import { SortFilterItemContainer } from "../../../common/reports/sortFilter/SortFilterItemContainer";
import { SortFilterContainer } from "../../../common/reports/sortFilter/SortFilterContainer";
import { SortFilterRowContainer } from "../../../common/reports/sortFilter/SortFilterRowContainer";
import { SortFilterTextField } from "../../../common/reports/sortFilter/SortFilterTextField";
import { SortFilterDateRange } from "../../../common/reports/sortFilter/SortFilterDateRange";
import useAllAssetTypes, { IAssetType } from "../../../../hooks/components/location/useAllAssetTypes";
import { LabelValue } from "../../../../types/common/LabelValue";
import { DateRangeChange } from "../../../../util/ReportDateRangeUtils";
import { LocationNameAutocomplete } from "../../../location/LocationNameAutocomplete";
import { BuyerNameAutocomplete } from "../../../buyer/BuyerNameAutocomplete";
import { SupplierInterchangeSortSelect, SupplierInterchangeSortByType } from "./SupplierInterchangeSortSelect";
import { AutocompleteOption } from "../../../SortFilter/interfaces";
import { AutocompleteChangeReason, Autocomplete, TextField } from "@mui/material";
import { SortDirType } from "../../../../types/common/SortDirType";
import { ChangeEvent } from "react";
import {useSnackbar} from "../../../../context/snackbar-context";

interface FilterProps {
    locationId: string;
    setLocationId: (value: string) => void;
    buyerId: string;
    setBuyerId: (value: string) => void;
    startDate: string;
    endDate: string;
    onDateRangeChange: (change: DateRangeChange) => void;
    applyDates: boolean;
    equipmentNumber: string;
    setEquipmentNumber: (value: string) => void;
    bookingNumber: string;
    setBookingNumber: (value: string) => void;
    activity: string;
    setActivity: (value: string) => void;
    assetType: string;
    setAssetType: (value: string) => void;
    driverName: string;
    setDriverName: (value: string) => void;
    sortBy: SupplierInterchangeSortByType;
    setSortBy: (value: SupplierInterchangeSortByType) => void;
    sortDir: SortDirType;
    setSortDir: (value: SortDirType) => void;
}

const SupplierInterchangeFilters = (props: FilterProps) => {
    const {
        locationId,
        setLocationId,
        buyerId,
        setBuyerId,
        startDate,
        endDate,
        onDateRangeChange,
        applyDates,
        equipmentNumber,
        setEquipmentNumber,
        bookingNumber,
        setBookingNumber,
        assetType,
        setAssetType,
        driverName,
        setDriverName,
        activity,
        setActivity,
        sortBy,
        setSortBy,
        sortDir,
        setSortDir,
    } = props;

    const { snackbarShowMessage } = useSnackbar();
    const assetTypeOptions: IAssetType[] = useAllAssetTypes(snackbarShowMessage);
    const assetTypeNames = assetTypeOptions.map(item => item.value);

    const activityOptions = [{ value: '', label: 'All' }, { value: 'CHECK_IN', label: 'Check In' }, { value: 'CHECK_OUT', label: 'Check Out' }];
    const activityNames = activityOptions.map((item: LabelValue) => item.label);

    // Convert stored value (key) to display value for Autocomplete
    const activityDisplayValue = activity === 'CHECK_IN' ? 'Check In' : activity === 'CHECK_OUT' ? 'Check Out' : 'All';
    const assetTypeDisplayValue = assetTypeOptions.find(item => item.key === assetType)?.value || 'All';

    const handleLocationChange = (_event: any, newValue: AutocompleteOption, reason: AutocompleteChangeReason) => {
        if (reason !== 'createOption') {
            setLocationId(newValue.id);
        }
    }

    const handleBuyerChange = (_event: any, newValue: AutocompleteOption, reason: AutocompleteChangeReason) => {
        if (reason !== 'createOption') {
            setBuyerId(newValue.id);
        }
    }

    const handleEquipmentNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
        setEquipmentNumber(event?.target?.value);
    }

    const handleBookingNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
        const numericInput = event?.target?.value.replace(/\D/g, "");
        setBookingNumber(numericInput);
    }

    const handleAssetTypeChange = (_: any, value: String | null) => {
        if (!value || value === 'All') {
            setAssetType('');
            return;
        }
        const asset = assetTypeOptions.find((item: any) => item?.value === value);
        if (asset) {
            setAssetType(asset.key);
        } else {
            setAssetType('');
        }
    }

    const handleActivityChange = (_: any, value: String | null) => {
        if (!value || value === 'All') {
            setActivity('');
            return;
        }
        const selectedActivity = activityOptions.find((item: any) => item?.label === value);
        if (selectedActivity) {
            setActivity(selectedActivity.value);
        } else {
            setActivity('');
        }
    }

    return <SortFilterContainer>
        {/* Row 1: Location, Date Range, Start Date, End Date */}
        <SortFilterRowContainer>
            <SortFilterItemContainer>
                <LocationNameAutocomplete value={locationId} onChange={handleLocationChange} />
            </SortFilterItemContainer>
            <SortFilterDateRange
                startDate={startDate}
                endDate={endDate}
                onChange={onDateRangeChange}
                disableFuture
                idSearchActive={!!bookingNumber.trim()}
                idSearchLabel="booking number"
                applyDates={applyDates}
            />
        </SortFilterRowContainer>
        {/* Row 2: Buyer, Equipment Number, Booking Number */}
        <SortFilterRowContainer>
            <SortFilterItemContainer>
                <BuyerNameAutocomplete value={buyerId} onChange={handleBuyerChange} />
            </SortFilterItemContainer>
            <SortFilterItemContainer>
                <SortFilterTextField
                    label={'Equipment Number'}
                    value={equipmentNumber}
                    onChange={handleEquipmentNumberChange}
                />
            </SortFilterItemContainer>
            <SortFilterItemContainer>
                <SortFilterTextField
                    label={'Booking Number'}
                    value={bookingNumber}
                    onChange={handleBookingNumberChange}
                />
            </SortFilterItemContainer>
        </SortFilterRowContainer>
        {/* Row 3: Asset Type, Activity, Driver Name, Sort By */}
        <SortFilterRowContainer>
            <SortFilterItemContainer sm={2.5}>
                <Autocomplete
                    options={assetTypeNames}
                    value={assetTypeDisplayValue}
                    renderInput={(params) => <TextField {...params} label='Asset Type' variant="standard" InputLabelProps={{ shrink: true }} />}
                    onChange={handleAssetTypeChange}
                />
            </SortFilterItemContainer>
            <SortFilterItemContainer sm={2.5}>
                <Autocomplete
                    options={activityNames}
                    value={activityDisplayValue}
                    renderInput={(params) => <TextField {...params} label="Activity" variant="standard" InputLabelProps={{ shrink: true }} />}
                    onChange={handleActivityChange}
                />
            </SortFilterItemContainer>
            <SortFilterItemContainer>
                <SortFilterTextField
                    label={'Driver Name'}
                    value={driverName}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setDriverName(e.target.value)}
                />
            </SortFilterItemContainer>
            <SortFilterItemContainer>
                <SupplierInterchangeSortSelect
                    sortBy={sortBy}
                    onSortByChange={setSortBy}
                    sortDir={sortDir}
                    onSortDirChange={setSortDir}
                />
            </SortFilterItemContainer>
        </SortFilterRowContainer>
    </SortFilterContainer>
}

export default SupplierInterchangeFilters;

